import React, { useState, useRef, useEffect, forwardRef } from "react";
import { Popover, Paper, IconButton, Box, Typography, InputAdornment } from "@mui/material";
import TreeViewComponent from "@CubeComponents/html/TreeView";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import IconComponent from "@CubeComponents/icons";
import { useTranslation } from "@i18n";
import Text from "@CubeComponents/html/Text/Text";
import type { SlickDataView } from "slickgrid-react";

/** One tree row as the grid's DataView hands it back. */
/** Exported so consumers can annotate their `onSelectionChange` handler, which is forwarded this
 *  row verbatim (the prop itself stays `any` — see the note on it below). */
export type TreeDropDownNode = { id?: string; label?: string; __hasChildren?: boolean };

/**
 * TreeViewComponent forwards its ref straight to the grid, so the handle is the SlickGrid
 * instance; only the DataView is driven from here.
 */
type TreeGridHandle = { dataView?: SlickDataView };

/**
 * @slickgrid-universal declares `getItemById` as total, but its DataView returns undefined for an
 * id that is no longer in the item map (e.g. a selection carried across a refresh). Stated here so
 * the caller's miss check stays live instead of being narrowed away by the vendor declaration.
 */
const getRowById = (dataView: SlickDataView, id: string): TreeDropDownNode | undefined =>
  dataView.getItemById<TreeDropDownNode>(id);

/** SlickGrid's row-selection CustomEvent as TreeViewComponent re-dispatches it. */
type SelectedRowIdsEvent = CustomEvent<{ args?: { selectedRowIds?: string[] } }>;

interface TreeSelectDropDownProps {
  /** Rows forwarded verbatim to TreeViewComponent; only `.length` is inspected here. */
  treeData?: object[];
  placeholder?: string;
  enableCheckboxSelection?: boolean;
  // UNFIXABLE without editing consumers: call sites annotate this handler with mutually
  // incompatible parameter types (`TemplateTreeNode | null` in InspectionCreation, a bare
  // `Function` in FileColumnsTreeView), so no single declared signature is assignable to all.
  /**
   * The selected row (`TreeDropDownNode`), forwarded verbatim.
   *
   * TRIED, and why each failed:
   *  - `(params: never)`: satisfies the annotated handlers but collapses the inline arrows in
   *    questionFieldsHelper / BoqItemUnitDetails to `never`, so their field reads fail.
   *  - METHOD SHORTHAND with an all-optional payload (the technique that closed
   *    `DropdownListComponentProps.onChange`): bivariance needs one direction to hold, and it does
   *    for `QueryFieldSelector.handleSelectionChange(params?: { field?: string })` once `field?` is
   *    declared. It does NOT hold for the two below, which is what still blocks this slot:
   *      1. FIXED - components/FileColumnsTreeView/index.tsx no longer declares `change: Function`.
   *      2. STILL BLOCKING - InspectionCreation.tsx:488
   *         `handleTemplateSelect(params?: TemplateTreeNode | null)`, where
   *         `TemplateTreeNode = TemplateTreeRow & { id?: ObjectIdString; label?: string }`.
   *         Every member is optional, so the weak-type check is satisfiable, but neither direction
   *         holds: payload -> param needs this payload's `id` to be assignable to the BRANDED
   *         `ObjectIdString` (a plain `string` is not), and param -> payload fails on the `| null`.
   *         Dropping `id` from the payload fixes the first but breaks the inline arrows in
   *         questionFieldsHelper and BoqItemUnitDetails, which both read `.id`.
   *
   * To close: drop `| null` from that handler's parameter (the component never passes null - it
   * returns early when nothing is selected) and brand the payload's `id`, or unbrand the node's.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- branded id + `| null` in one parameter closes both variance directions; see comment above
  onSelectionChange?: any;
  value?: string | null;
  disabled?: boolean;
  onSelectedRowIdsChanged?: (e: SelectedRowIdsEvent) => void;
  disabledParentSelection?: boolean;
  allowDeselection?: boolean;
  popoverHeight?: string | number;
  popoverWidth?: string | number;
  selectCheckBoxOnRowClick?: boolean;
  inputFieldHeight?: string;
  /** Forwarded through `...rest` to TreeViewComponent. */
  multiple?: boolean;
  /** Forwarded through `...rest` to TreeViewComponent. */
  showCrossIcon?: boolean;
}

const TreeSelectDropDown = forwardRef(
  (
    {
      treeData = [],
      placeholder = "Choose an option...",
      enableCheckboxSelection = false,
      onSelectionChange,
      value = "",
      disabled = false,
      onSelectedRowIdsChanged,
      disabledParentSelection = false,
      allowDeselection = true,
      popoverHeight = "28.125rem",
      popoverWidth = "18.75rem",
      selectCheckBoxOnRowClick,
      inputFieldHeight = "auto",
      ...rest
    }: TreeSelectDropDownProps,
    ref: React.Ref<TreeGridHandle>,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValues, setSelectedValues] = useState(value);
    const textFieldRef = useRef<HTMLInputElement>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const innerRef = useRef<TreeGridHandle>(null);
    // The handle is read back here through `.current`, which only a ref object exposes; a caller
    // that passes a callback ref gets the internal object instead of a `.current` that is never set.
    const treeRef = typeof ref === "function" ? innerRef : (ref ?? innerRef);
    const { t } = useTranslation();
    const lastSelectedIdRef = useRef<string | null>(null);
    const handleRowSelection = (e: SelectedRowIdsEvent) => {
      const selectedRowIds: string[] = e.detail.args?.selectedRowIds ?? [];
      const dv = treeRef.current?.dataView;
      if (!dv) return;

      // Prevent deselection when allowDeselection is false
      if (selectedRowIds.length === 0) {
        if (!allowDeselection && lastSelectedIdRef.current) {
          dv.setSelectedIds([lastSelectedIdRef.current]);
        }
        return;
      }

      const selectedId = selectedRowIds[0];
      // Non-empty by the length check above, so index 0 exists.
      const item = getRowById(dv, selectedId!);
      if (!item) return;

      if (disabledParentSelection && item.__hasChildren) {
        e.stopPropagation();
        // Restore previous selection if any, otherwise clear
        dv.setSelectedIds(lastSelectedIdRef.current ? [lastSelectedIdRef.current] : []);
        return;
      }

      lastSelectedIdRef.current = selectedId ?? null;
      handleTreeSelection(item);

      if (onSelectedRowIdsChanged) {
        onSelectedRowIdsChanged(e);
      }
    };
    const handleTextFieldClick = (e: React.MouseEvent<HTMLElement>) => {
      if (disabled) return;
      setIsOpen(true);
      setAnchorEl(e.currentTarget);
    };
    const handleClose = (_event: unknown, reason?: string) => {
      // Only close on backdrop click or escape key — not on focus/DOM changes from tree expand/collapse
      if (reason === "backdropClick" || reason === "escapeKeyDown") {
        setIsOpen(false);
        setAnchorEl(null);
      }
    };
    const closeDropdown = () => {
      setIsOpen(false);
      setAnchorEl(null);
    };
    const handleTreeSelection = (params: TreeDropDownNode) => {
      // @ts-expect-error -- `params.label` is optional on `TreeDropDownNode` but the selected-value state is `string`. REAL GAP: selecting a node with no label would store `undefined`.
      setSelectedValues(params.label);
      closeDropdown();
      if (onSelectionChange) onSelectionChange(params);
    };
    useEffect(() => {
      setSelectedValues(value);
    }, [value]);
    return (
      <>
        <TextFieldComponent
          inputRef={textFieldRef}
          placeholder={placeholder}
          disabled={disabled}
          value={selectedValues}
          onClick={handleTextFieldClick}
          height={inputFieldHeight}
          sx={{
            "& .MuiInputBase-input": {
              cursor: "pointer",
            },
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" size="small" disabled={disabled}>
                    <IconComponent
                      name="DropdownIcon"
                      width="1rem"
                      height="1rem"
                      className={`transition-transform duration-200 w-4 h-4 ${isOpen ? "rotate-180" : "rotate-0"}`}
                    />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                cursor: "pointer",
                "& input": {
                  cursor: "pointer",
                },
              },
            },
            htmlInput: {
              readOnly: true,
            },
          }}
        />
        <Popover
          open={isOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          slotProps={{
            paper: {
              sx: {
                width: popoverWidth,
                maxHeight: popoverHeight,
                mt: 1,
                boxShadow: "0rem 0.125rem 0.25rem rgba(0, 0, 0, 0.09)",
              },
            },
          }}
        >
          <Paper>
            <Box>
              {treeData.length > 0 ? (
                <TreeViewComponent
                  ref={treeRef}
                  treeData={treeData}
                  onCheckChange={handleTreeSelection}
                  enableCheckboxSelection={enableCheckboxSelection}
                  height={popoverHeight}
                  width="100%"
                  // KNOWN BUG, preserved verbatim: `popoverWidth` defaults to the string
                  // "18.75rem", so this arithmetic yields NaN unless a caller passes a number.
                  columnWidth={Number(popoverWidth) - 32}
                  enableFiltering
                  enableSearch
                  disableMarginForNoData
                  onSelectedRowIdsChanged={handleRowSelection}
                  selectCheckBoxOnRowClick={selectCheckBoxOnRowClick ?? true}
                  columnId="label"
                  identifierPropName="id"
                  parentPropName="parentId"
                  disableHeader={true}
                  disabledParentSelection
                  showPageCount={false}
                  {...rest}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: popoverHeight,
                    color: "text.secondary",
                  }}
                >
                  <Typography variant="body2">
                    <Text>{t("No items found")}</Text>
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Popover>
      </>
    );
  },
);
TreeSelectDropDown.displayName = "TreeSelectDropDown";
export default TreeSelectDropDown;
